import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, LogIn, Globe, Radio, Activity, MapPin, Monitor } from "lucide-react";

const roleColor = {
  admin: "text-status-critical border-status-critical/40 bg-status-critical/10",
  instructor: "text-status-warning border-status-warning/40 bg-status-warning/10",
  trainee: "text-primary border-primary/40 bg-primary/10",
};

function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return isNaN(d) ? "—" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-card border border-border/60 rounded-md p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-ot">{label}</span>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-mono text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => toast.error("Failed to load analytics"));
  }, []);

  if (!data) {
    return <Layout><div className="font-mono text-muted-foreground text-sm py-20 text-center animate-pulse">LOADING ANALYTICS…</div></Layout>;
  }

  const { kpis, logins_by_day, top_ips, recent_logins, users } = data;

  return (
    <Layout>
      <div className="mb-6">
        <div className="label-ot text-status-critical">Admin · Restricted</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Access &amp; Traffic Analytics</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          Who has signed in, from which IP addresses, and on which devices. Visible to administrators only.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Kpi icon={Users} label="Total Users" value={kpis.total_users} accent="text-primary" />
        <Kpi icon={Activity} label="Logged-in Users" value={kpis.logged_in_users} accent="text-status-normal" />
        <Kpi icon={LogIn} label="Total Logins" value={kpis.total_logins} accent="text-status-warning" />
        <Kpi icon={Globe} label="Unique IPs" value={kpis.unique_ips} accent="text-status-critical" />
        <Kpi icon={Radio} label="Active Sessions" value={kpis.active_sessions} accent="text-status-normal" />
      </div>

      {/* Logins over time */}
      <div className="bg-card border border-border/60 rounded-md mb-8">
        <div className="px-4 py-2.5 border-b border-border/60 bg-popover"><span className="label-ot">Logins · Last 14 Days</span></div>
        <div className="p-3" style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={logins_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 4, fontSize: 11 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="logins" fill="#007AFF" radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Recent logins */}
        <section>
          <h2 className="font-heading text-lg font-semibold mb-3">Recent Logins</h2>
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-3 py-2.5">User</th><th className="px-3 py-2.5">IP</th>
                <th className="px-3 py-2.5 hidden sm:table-cell">Device</th><th className="px-3 py-2.5 text-right">When</th>
              </tr></thead>
              <tbody>
                {recent_logins.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-muted-foreground text-sm">No logins recorded yet.</td></tr>
                ) : recent_logins.map((e, i) => (
                  <tr key={i} data-testid="recent-login-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-3 py-2.5">
                      <div className="font-medium truncate max-w-[140px]">{e.name || "—"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[140px]">{e.email}</div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs flex items-center gap-1.5"><MapPin className="w-3 h-3 text-muted-foreground" />{e.ip}</td>
                    <td className="px-3 py-2.5 hidden sm:table-cell text-xs text-muted-foreground"><span className="inline-flex items-center gap-1.5"><Monitor className="w-3 h-3" />{e.device}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono text-[11px] text-muted-foreground">{fmt(e.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Top IPs */}
        <section>
          <h2 className="font-heading text-lg font-semibold mb-3">Top Source IPs</h2>
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/60 text-left label-ot">
                <th className="px-3 py-2.5">IP Address</th><th className="px-3 py-2.5 text-right">Logins</th>
                <th className="px-3 py-2.5 text-right">Users</th><th className="px-3 py-2.5 text-right">Last Seen</th>
              </tr></thead>
              <tbody>
                {top_ips.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-muted-foreground text-sm">No traffic yet.</td></tr>
                ) : top_ips.map((r) => (
                  <tr key={r.ip} data-testid="top-ip-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-3 py-2.5 font-mono text-xs">{r.ip}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.logins}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{r.users}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[11px] text-muted-foreground">{fmt(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Users access table */}
      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Users &amp; Last Access</h2>
        <div className="bg-card border border-border/60 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border/60 text-left label-ot">
              <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5 hidden sm:table-cell">Email</th>
              <th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5 text-right">Logins</th>
              <th className="px-4 py-2.5 text-right hidden md:table-cell">Last IP</th><th className="px-4 py-2.5 text-right">Last Login</th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} data-testid="user-access-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                  <td className="px-4 py-2.5 font-medium">{u.name || "—"}</td>
                  <td className="px-4 py-2.5 hidden sm:table-cell font-mono text-xs text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${roleColor[u.role] || ""}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{u.logins}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground hidden md:table-cell">{u.last_ip || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[11px] text-muted-foreground">{fmt(u.last_login)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
